<template>
  <InnerContainer :title="templateData.pageTitle" :sub-title="templateData.problem">
    <template #default>
      <div
        v-for="(rule, ruleIndex) in templateData.content[0].count1List"
        :key="ruleIndex"
        class="adverb-formation"
      >
        <div class="table-title">
          <div class="table-title-text">
            <div
              class="table-title-text-inner"
              @click="handleClick"
              v-html="processSyntax(rule.syntax, ruleIndex)"
            ></div>
          </div>
        </div>
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th v-for="wordClass in getWordClasses(rule.count2List)" :key="wordClass">
                  {{ wordClass }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rowIndex in getMaxRowCount(rule.count2List)" :key="rowIndex">
                <td v-for="wordClass in rule.count2List" :key="wordClass.wordClass">
                  <span
                    v-if="wordClass.count3List[rowIndex - 1]"
                    v-html="processContent(wordClass.count3List[rowIndex - 1].content)"
                  ></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </InnerContainer>
</template>

<script setup lang="ts">
import InnerContainer from "@/views/grammar/newGrammar/components/InnerContainer.vue";
import { defineProps, ref, computed } from "vue";
import fillIcon from "@/assets/grammar/fill_icon.png";

const props = defineProps({
  templateData: {
    type: Object,
    required: true,
  },
});

// 响应式状态，跟踪每个规则的显示/隐藏状态
const hiddenStates = ref<{ [key: number]: boolean }>({});

// 初始化隐藏状态
const initHiddenStates = () => {
  const count1List = props.templateData.content[0].count1List;
  count1List.forEach((_: any, index: number) => {
    hiddenStates.value[index] = true; // 默认显示
  });
};

// 在组件挂载时初始化状态
initHiddenStates();

// 切换显示/隐藏状态
const toggleVisibility = (ruleIndex: number) => {
  hiddenStates.value[ruleIndex] = !hiddenStates.value[ruleIndex];
};

// 处理语法说明中的特殊标签
const processSyntax = (syntax: string, ruleIndex: number) => {
  return syntax
    .replace(
      /<d>/g,
      `<span class="interactive-text underlined" data-rule-index="${ruleIndex}"><span class="text-content ${hiddenStates.value[ruleIndex] ? "hidden" : ""}">`
    )
    .replace(
      /<\/d>/g,
      `</span><span class="underline-placeholder"></span><img class="inline-toggle-icon" src="${fillIcon}" alt="" data-rule-index="${ruleIndex}" /></span>`
    )
    .replace(/<u>/g, "<u>")
    .replace(/<\/u>/g, "</u>")
    .replace(/<b>/g, "<strong>")
    .replace(/<\/b>/g, "</strong>")
    .replace(/<i>/g, "<em>")
    .replace(/<\/i>/g, "</em>")
    .replace(/<br\/>/g, "<br/>");
};

// 事件委托处理点击
const handleClick = (event: Event) => {
  const target = event.target as HTMLElement;
  if (target.classList.contains("inline-toggle-icon")) {
    const ruleIndex = target.getAttribute("data-rule-index");
    if (ruleIndex !== null) {
      toggleVisibility(parseInt(ruleIndex));
    }
  }
};

// 处理内容中的特殊标签
const processContent = (content: string) => {
  return content
    .replace(/<r>/g, '<span class="highlight-red">')
    .replace(/<\/r>/g, "</span>")
    .replace(/<l>/g, '<span class="highlight-blue">')
    .replace(/<\/l>/g, "</span>")
    .replace(/<y>/g, '<span class="highlight-yellow">')
    .replace(/<\/y>/g, "</span>")
    .replace(/<bl>/g, '<span class="highlight-blue">')
    .replace(/<\/bl>/g, "</span>")
    .replace(/<g>/g, '<span class="highlight-green">')
    .replace(/<\/g>/g, "</span>")
    .replace(/<u>/g, "<u>")
    .replace(/<\/u>/g, "</u>")
    .replace(/<b>/g, "<strong>")
    .replace(/<\/b>/g, "</strong>")
    .replace(/<i>/g, "<em>")
    .replace(/<\/i>/g, "</em>")
    .replace(/<br\/>/g, "<br/>");
};

// 获取所有词性
const getWordClasses = (count2List: any[]) => {
  return count2List.map((item) => item.wordClass);
};

// 获取最大行数
const getMaxRowCount = (count2List: any[]) => {
  return Math.max(...count2List.map((item) => item.count3List.length));
};
</script>

<style scoped lang="less">
.problem-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  border-left: 4px solid #007bff;

  p {
    margin: 0;
    font-size: 1.1rem;
    color: #333;
    line-height: 1.5;
  }
}

.adverb-formation {
  background-color: #f5f5f5;
  border-radius: 0.5rem;
  padding: 1rem;
  margin: 1.01rem 0;
  transition: all 0.3s ease;
  font-weight: 400;

  .table-title {
    margin-bottom: 1rem;
    font-size: 1.12rem;
    color: #1e2023;
    display: flex;
    align-items: flex-start;

    &-text {
      flex: 1;
      margin-left: 0.5rem;
      padding-bottom: 0.5rem;
      font-weight: 400;
      display: flex;
      &-inner {
        flex: 1;
        display: block; /* 改为 block 以允许换行 */
        word-wrap: break-word; /* 允许长单词换行 */
        word-break: break-word; /* 在合适的位置换行 */
        line-height: 1.5; /* 增加行高以提高可读性 */
      }
    }
  }

  .table-container {
    overflow-x: auto;
    margin-bottom: 1.01rem;
    transition: all 0.3s ease;

    table {
      width: 100%;
      border-collapse: collapse;

      th,
      td {
        min-width: 5rem;
        word-break: break-all;
        padding: 0.3rem;
        text-align: center;
        border: 1px solid #cdd1d4;
        font-size: 1.12rem;
        color: #3f4140;
        font-weight: 400;
      }

      th {
        background-color: #f2f3f6;
        font-weight: 400;
      }
    }
  }
}

:deep(.highlight-orange) {
  color: #ff8c00;
  font-weight: 500;
}

:deep(.highlight-blue) {
  color: #299fe5;
  font-weight: 500;
}

:deep(.highlight-red) {
  color: #dc3545;
  font-weight: 500;
}

:deep(.highlight-yellow) {
  color: #ffc107;
  font-weight: 500;
}

:deep(.highlight-green) {
  color: #0dcb8b;
  font-weight: 500;
}

:deep(.inline-toggle-icon) {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  margin-left: 0.2rem;
  vertical-align: middle;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
}

:deep(.interactive-text) {
  cursor: pointer;
  position: relative;
  display: inline; /* 保持 inline 以允许换行 */

  &::after {
    display: none; /* 隐藏原来的伪元素下划线 */
  }
}

:deep(.text-content) {
  display: inline; /* 保持 inline 以允许换行 */
  padding: 0 0.8rem;
  transition: opacity 0.3s ease;
  position: relative;
  font-weight: 400;
  color: #1e2023; /* 默认文字颜色为 #1e2023 */
  text-decoration-color: #1e2023; /* 下划线颜色始终为 #1e2023 */
  text-underline-offset: 0.2rem; /* 下划线与文字的距离 */
  border-bottom: 0.1rem solid #1e2023;

  &.hidden {
    color: #f5f5f5;
  }
}

:deep(.underline-placeholder) {
  display: none; /* 隐藏占位符，只用于保持结构 */
}

:deep(.inline-toggle-icon) {
  width: 1.21rem;
  height: 1.21rem;
  cursor: pointer;
  vertical-align: middle;
  transition: transform 0.3s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.1);
  }
}
</style>
